import React, { PureComponent } from 'react'
import classnames from 'classnames'
import moment from 'moment'
import { FormattedMessage, injectIntl } from 'react-intl'
import TimeAgo from 'react-timeago'
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter'
import englishStrings from 'react-timeago/lib/language-strings/en'
import frenchStrings from 'react-timeago/lib/language-strings/fr'

import { truncate, getMimeType, logError } from 'src/utils/helpers'
import { postRequest } from 'src/utils/request-client'
import Loader from 'src/components/Loader/Loader'
import SvgIcon from 'src/components/SvgIcon'
import chatService from 'src/utils/ChatService'

import styles from '../styles.module.less'

class AttachmentMessageType extends PureComponent {
  constructor(props) {
    super(props)
    const isAttachmentUploading = this.isAttachmentUploading(props.message)
    this.state = {
      message: props.message,
      retrySendMessage: false,
      retryUpload: false,
      sendingMessage: false,
      isAttachmentUploading,
    }
    isAttachmentUploading && this.uploadAttachment(props.message)
  }

  uploadAttachment = attachment => {
    const contentType = getMimeType(attachment.attributes.contentType)
    const formData = new FormData()
    formData.append('file', attachment.file)
    formData.append('contentType', contentType)

    postRequest({ base: '/files/upload', mock: false }, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        this.setState(
          prevState => ({
            message: {
              ...prevState.message,
              body: response.url,
              uploading: false,
            },
            isAttachmentUploading: false,
            sendingMessage: true,
          }),
          () => {
            this.sendMessage(this.state.message)
          },
        )
      })
      .catch(e => {
        this.setState(prevState => ({
          message: {
            ...prevState.message,
            uploading: false,
          },
          isAttachmentUploading: false,
          retryUpload: true,
        }))
        logError('Upload attachment failed', e)
      })
  }

  sendMessage = async message => {
    chatService
      .sendMessage(message.body, 'file', this.props.userEmail)
      .then(sendResponse => {
        if (sendResponse === false) {
          // If message not actually sent (while offline or some other issue)
          this.setState({
            sending: false,
            retry: true,
          })
        } else {
          this.setState(prevState => {
            return {
              sendingMessage: false,
              retrySendMessage: false,
              message: { ...prevState.message, timestamp: moment.now() },
            }
          })
        }
      })
      .catch(e => {
        logError('Send message attachment', e)
        this.setState({
          sendingMessage: false,
          // Show retry only if needed (it's not needed while re-trying to send 10 times)
          retrySendMessage:
            e && e.hasOwnProperty('shouldOfferRetry') && e.shouldOfferRetry === false
              ? false
              : true,
        })
      })
  }

  retryOrOpen = () => {
    const { message, retryUpload, retrySendMessage } = this.state
    if (retrySendMessage) {
      this.retrySendMessage()
      return
    }

    if (retryUpload) {
      this.retryUpload()
      return
    }

    if (message.body) {
      window.open(message.body, '_blank')
    }
  }

  retrySendMessage = () => {
    const { message, retrySendMessage } = this.state
    if (!retrySendMessage) {
      return
    }
    this.setState({
      sendingMessage: true,
    })
    this.sendMessage(message)
  }

  retryUpload = () => {
    const { retryUpload, message } = this.state
    if (!retryUpload) {
      return
    }
    this.setState(prevState => {
      return {
        message: {
          ...prevState.message,
          uploading: true,
        },
      }
    })
    this.uploadAttachment(message)
  }

  isAttachmentUploading = message => {
    if (!message) {
      return false
    }
    // Original message don't have uploaded property
    if (typeof message.uploading === 'undefined') {
      return false
    }

    return message.uploading || false
  }

  render() {
    const { message, sendingMessage, retrySendMessage, retryUpload } = this.state
    const { outgoing, intl, renderAuthor } = this.props
    const isAttachmentUploading = this.isAttachmentUploading(message)

    const formatter =
      intl && intl.locale === 'fr' ? buildFormatter(frenchStrings) : buildFormatter(englishStrings)

    return (
      <div
        className={classnames(styles.text, styles.textAttachment, {
          [styles.cursor]: retryUpload || retrySendMessage,
        })}
        onClick={this.retryOrOpen}
        data-qa='chat__message__content'
      >
        <div
          className={classnames(
            styles.messageItem,
            styles.messageItemContainer,
            styles.messageItemAttachmentContainer,
            { [styles.link]: true, messageItemColor: outgoing },
          )}
        >
          <div
            // eslint-disable-next-line react/no-unknown-property
            display-if={isAttachmentUploading}
            className={styles.loaderOverlay}
          />
          <Loader
            display-if={isAttachmentUploading}
            className={styles.spinnerInsideLoader}
            spinnerClassName={styles.spinnerInside}
          />
          {isAttachmentUploading ? (
            <FormattedMessage
              id='app.pictures.uploadingAttachment'
              defaultMessage='Uploading attachment'
            />
          ) : (
            <span>
              <SvgIcon size='xs' type='AttachIcon' addClassName={styles.attachIcon} />
              {truncate(message.body, 60)}
            </span>
          )}
        </div>
        <div
          className={styles.status}
          // eslint-disable-next-line react/no-unknown-property
          display-if={sendingMessage}
          data-qa='chat__message__status chat__message__status--sending'
        >
          <FormattedMessage id='app.chat.message.sending' defaultMessage='Sending' />
        </div>
        <div
          className={classnames(styles.status, { [styles.errorSending]: true })}
          // eslint-disable-next-line react/no-unknown-property
          display-if={!sendingMessage && retrySendMessage}
          data-qa='chat__message__status chat__message__status--error'
        >
          <FormattedMessage
            id='app.chat.message.couldNotSendAttachment'
            defaultMessage="Couldn't send an attachment"
          />
        </div>
        <div
          className={styles.status}
          // eslint-disable-next-line react/no-unknown-property
          display-if={isAttachmentUploading}
          data-qa='chat__message__status chat__message__status--uploading'
        >
          <FormattedMessage id='app.pictures.uploading' defaultMessage='Uploading...' />
        </div>
        <div
          className={classnames(styles.status, { [styles.errorSending]: true })}
          // eslint-disable-next-line react/no-unknown-property
          display-if={!isAttachmentUploading && retryUpload}
          data-qa='chat__message__status chat__message__status--error'
        >
          <FormattedMessage id='app.pictures.couldNotUpload' defaultMessage="Couldn't upload" />
        </div>
        <div
          className={styles.status}
          // eslint-disable-next-line react/no-unknown-property
          display-if={!isAttachmentUploading && !sendingMessage && !retrySendMessage}
          data-qa='chat__message__status chat__message__status--time'
        >
          {renderAuthor}
          <TimeAgo date={message.timestamp} formatter={formatter} />
        </div>
      </div>
    )
  }
}

export default injectIntl(AttachmentMessageType)
